import { DateTime, Duration } from 'luxon';
import { PaginationRequest } from '../pagination/pagination';
import { Product, ProductInventoryUnit } from '../product/product';
import { User, UserEquipment } from '../user/user';
import { Invoice } from '../invoice/invoice';
import { Admin } from '../admin/admin';

export type WorkOrder = {
  readonly id: string;
  readonly orderNumber: string | undefined;
  readonly poNumber: string | undefined;
  readonly locationId: number | undefined;
  readonly from: DateTime;
  readonly to: DateTime | undefined;
  readonly actualStart: DateTime | undefined;
  readonly actualEnd: DateTime | undefined;
  readonly product: Product | undefined;
  readonly unit: ProductInventoryUnit | undefined;
  readonly unitStatus: UseabilityStatus | undefined;
  readonly user: User | undefined;
  readonly userEquipment: UserEquipment | undefined;
  readonly equipmentDescription: string | undefined;
  readonly type: WorkOrderType | undefined;
  readonly status: WorkOrderStatus;
  readonly actualCost: number | undefined;
  readonly hourStart: DateTime | undefined;
  readonly notes: string | undefined;
  readonly invoices: Invoice[] | undefined;
  readonly workOrderProducts: WorkOrderProduct[];
  readonly createdAt: DateTime;
  readonly updatedAt: DateTime;
  readonly latestNote?: WorkOrderNoteFile;
  readonly mechanic?: Admin;
};

export type WorkOrderType = {
  readonly id: string;
  readonly label: string;
};

export type WorkOrderNoteFile = {
  readonly id: string;
  readonly workOrderId: string;
  readonly note?: string;
  readonly file?: string;
  readonly uploadedBy: {
    adminId: number;
    adminFullName: string;
  };
  readonly uploadedAt: DateTime;
};

export type WorkOrderProduct = {
  readonly id: string;
  readonly workOrderId: string;
  readonly product: Product;
  readonly quantity: number;
  readonly customerUnitPrice: number;
  readonly description: string | undefined;
  readonly createdAt: DateTime;
  readonly updatedAt: DateTime;
};

export enum WorkOrderStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Completed = 'Completed',
  OnHold = 'On Hold',
  Cancelled = 'Cancelled',
}

export type WorkOrderProductInput = {
  readonly id?: string;
  readonly productId: number;
  readonly customerUnitPrice: number | undefined;
  readonly quantity: number;
  readonly description: string | undefined;
};

export type FleetWorkOrderRequest = {
  readonly from: DateTime;
  readonly to: DateTime | undefined;
  readonly locationId: number;
  readonly productId: number;
  readonly unitId: number;
  readonly unitStatus: string;
  readonly type: string | undefined;
  readonly notes: string | undefined;
  readonly poNumber: string | undefined;
};

export type CustomerWorkOrderRequest = {
  readonly from: DateTime;
  readonly to: DateTime | undefined;
  readonly locationId: number;
  readonly userId: number;
  readonly userEquipmentId: string;
  readonly type: string | undefined;
  readonly notes: string | undefined;
  readonly poNumber: string | undefined;
};

export type UpdateWorkOrderRequest = {
  readonly id: string;
  readonly from: DateTime | undefined;
  readonly to: DateTime | undefined;
  readonly type: string | undefined;
  readonly unitStatus: string | undefined;
  readonly equipmentDescription: string | undefined;
  readonly locationId: number | undefined;
  readonly mechanicId: number | undefined;
  readonly poNumber: string | undefined;
  readonly notes: string | undefined;
};

export type UpdateWorkOrderStatusRequest = {
  readonly id: string;
  readonly status: string | undefined;
  readonly actualStart: DateTime | undefined;
  readonly actualEnd: DateTime | undefined;
  readonly actualCost: number | undefined;
  readonly hourReading: number | undefined;
};

export enum UseabilityStatus {
  SoftDown = 'Soft Down',
  HardDown = 'Hard Down',
}

export class WorkOrderSearchRequest extends PaginationRequest {
  keyword?: string;
  locationIds?: number[];
  userId?: number;
  statuses?: string[];
  from?: DateTime;
  to?: DateTime;
  sortBy?: 'from' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
}

export class WorkOrderSearchParams extends WorkOrderSearchRequest {
  workOrderTab?: 'current' | 'history' | 'late' | 'upcoming';
}

export type WorkOrderExportRequest = Omit<
  WorkOrderSearchRequest,
  'pageNumber' | 'pageSize' | 'sortBy' | 'sortDirection'
>;

export class ScheduledService {
  id!: string;
  serviceName!: string;
  workOrderType!: WorkOrderType;
  unitStatus!: UseabilityStatus;
  duration?: Duration;
  frequencyInterval?: Duration;
  machineHoursInterval?: number;
  createdAt!: DateTime;
  createdBy!: number;
  updatedAt!: DateTime;
  updatedBy!: number;
  deleted!: boolean;
  productUnits!: { [productId: number]: { name: string; units: ScheduledServiceProductUnit[] } };
}

export class ScheduledServiceProductUnit {
  productUnit!: ProductInventoryUnit;
  initialServiceDate?: DateTime;
  initialServiceHours?: number;
}

export class ScheduledServiceRequest {
  serviceName!: string;
  workOrderTypeId!: string;
  unitStatus!: UseabilityStatus;
  duration?: Duration;
  frequencyInterval?: Duration;
  machineHoursInterval?: number;
  productUnitIds!: number[];
  productUnits!: { productUnitId: number; initialServiceDate?: DateTime; initialServiceHours?: number }[];
}

export class ScheduledServiceSearchRequest extends PaginationRequest {
  keyword?: string;
}
